import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from '../../_objects/subject';
import { SubjectModelListItem } from '../../_objects/subject-model';
import { SubjectModelService } from '../../_services/http-api/subject-model.service';
import { SubjectService } from '../../_services/http-api/subject.service';
import { TenantConfigService } from '../../_services/http-api/tenant-config.service';
import { SearchSelectOption } from '../../_shared/component/search-combo-box/search-select-option';
import {
  ComboBoxOption,
  FilterData,
  FilterSelectDialogComponent
} from '../../_shared/dialog/filter-select-dialog/filter-select-dialog.component';
import { FILTER_CONTENT_ID, INSPECTION_STATUS, TenantConfig } from '../../app.constants';

export type InspectionListSort = { value: null | 'inspectionDate' | 'title', direction: null | 'asc' | 'desc'}

@Component({
    selector: 'app-inspection-list-filter',
    templateUrl: './inspection-list-filter.component.html',
    styleUrl: './inspection-list-filter.component.scss',
    standalone: false
})
export class InspectionListFilterComponent implements OnInit {
  public filterData: FilterData[] = [];
  public sortData: InspectionListSort = { value: 'inspectionDate', direction: 'desc' };

  @Input() disableFilters: boolean = false;

  @Output() public filtersChanged: EventEmitter<FilterData[]> = new EventEmitter();
  @Output() public sortChanged: EventEmitter<InspectionListSort> = new EventEmitter();

  private subjects: Subject[] = [];
  private subjectsModel: SubjectModelListItem[] = [];
  private serialNumberLabel = TenantConfig.DefaultConfigurationValues.LabelOfSerialNumber;

  constructor(
    private subjectModelService: SubjectModelService,
    private subjectService: SubjectService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private tenantConfigService: TenantConfigService) {}

  ngOnInit(): void {
    this.getSubjectsData();
    this.getSubjectsModelData();
    this.setConfig();
  }

  public get isTitleAsc() {
    return this.sortData.value == 'title' && this.sortData.direction == 'asc';
  }

  public get isDateAsc() {
    return this.sortData.value == 'inspectionDate' && this.sortData.direction == 'asc';
  }

  public changeSortByDate(direction: 'asc' | 'desc') {
    this.sortData = { value: 'inspectionDate', direction };
    this.sortChanged.emit(this.sortData);
  }

  public changeSortByTitle(direction: 'asc' | 'desc') {
    this.sortData = { value: 'title', direction };
    this.sortChanged.emit(this.sortData);
  }

  private setConfig() {
    this.tenantConfigService
      .getTenantConfigs()
      .pipe(take(1))
      .subscribe(configs => {
        if (configs) {
          const snLabelConfig = configs.find(_ => _.key === TenantConfig.LabelOfSerialNumber.KEY);
          if (snLabelConfig) {
            this.serialNumberLabel = snLabelConfig.value as string;
          }
        }
      });
  }

  private getSubjectsData() {
    this.subjectService.getSubjects().subscribe(val => this.subjects = val);
  }

  private getSubjectsModelData() {
    this.subjectModelService.getSubjectModels().subscribe(val => this.subjectsModel = val);
  }

  private getSelectedIdFor(id: string) {
    return this.filterData.find(filter => filter.id == id)?.value;
  }

  public getFiltersOptions() {
    const defaultOpt = new SearchSelectOption(null, this.translate.instant('inspectionlist.all'));

    const statusValues = Array.from(INSPECTION_STATUS.keys()).sort().map(st =>
      new SearchSelectOption(st, this.translate.instant('inspectionlist.status_' + st.toString().toLowerCase().replace(' ', ''))));

    const subjectModelOption = this.subjectsModel.map(_ => new SearchSelectOption(_.id, _.name)).sort();
    const subjectOption = this.subjects.map(subject => new SearchSelectOption(subject.id, subject.serialNo));

    const generateOptions = (subjects: Subject[], propertyName: keyof Subject)  =>  subjects
      .filter(subject => subject[propertyName] !== '' && !!subject[propertyName])
      .map(subject => new SearchSelectOption(subject[propertyName].toString(), subject[propertyName].toString()))
      .reduce((acc, currentValue) => {
        if (acc.find(o => o.label === currentValue.label) == null) {
          return [...acc, currentValue];
        }
        return acc;
      }, [] as SearchSelectOption[]);

    const ownerOption = generateOptions(this.subjects, 'owner');
    const countryOptions = generateOptions(this.subjects, 'country');
    const stateOptions = generateOptions(this.subjects, 'state');
    const districtOptions = generateOptions(this.subjects, 'district');
    const siteOptions = generateOptions(this.subjects, 'site');
    const projectOptions = generateOptions(this.subjects, 'project');

    return [
      { id: FILTER_CONTENT_ID.STATUS, label: this.translate.instant('inspectionlist.status'), options: [defaultOpt, ...statusValues], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.STATUS) },
      { id: FILTER_CONTENT_ID.SUBJECT_MODEL, label: this.translate.instant('inspectionlist.model'), options: [defaultOpt, ...subjectModelOption], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.SUBJECT_MODEL) },
      { id: FILTER_CONTENT_ID.SUBJECT, label: this.serialNumberLabel, options: [defaultOpt, ...subjectOption], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.SUBJECT) },
      { id: FILTER_CONTENT_ID.OWNER, label: this.translate.instant('inspectionlist.owner'), options: [defaultOpt, ...ownerOption], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.OWNER) },
      { id: FILTER_CONTENT_ID.COUNTRY, label: this.translate.instant('inspectionlist.country'), options: [defaultOpt, ...countryOptions], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.COUNTRY) },
      { id: FILTER_CONTENT_ID.IS_OWN, label: this.translate.instant('inspectionlist.myInspections'), options: [], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.IS_OWN), isCheckbox: true },
      { id: FILTER_CONTENT_ID.STATE, label: this.translate.instant('inspectionlist.state'), options: [defaultOpt, ...stateOptions], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.STATE) },
      { id: FILTER_CONTENT_ID.DISTRICT, label: this.translate.instant('inspectionlist.district'), options: [defaultOpt, ...districtOptions], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.DISTRICT) },
      { id: FILTER_CONTENT_ID.SITE, label: "Site", options: [defaultOpt, ...siteOptions], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.SITE) },
      { id: FILTER_CONTENT_ID.PROJECT, label: "Project", options: [defaultOpt, ...projectOptions], selected: this.getSelectedIdFor(FILTER_CONTENT_ID.PROJECT) },
    ].map(op => new ComboBoxOption(op.id, op.label, op.options, op.selected, op?.isCheckbox));
  }

  openAddFilterDialog() {
    const dialogRef = this.dialog.open(FilterSelectDialogComponent, {
      width: '560px',
      panelClass: 'dialog-scroll',
      data: {
        comboBoxOptions: this.getFiltersOptions()
      }
    });

    dialogRef.componentInstance.changedFilter
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(result => {
        result = result.filter((f) => f.label !== undefined);
        this.filterData = result;
        this.filtersChanged.emit(result);
        dialogRef.close();
      });

  }

  clearFilter(filter: FilterData) {
    this.filterData = this.filterData.filter(f => f.id !== filter.id);
    this.filtersChanged.emit(this.filterData);
  }
}
